import { trpc } from "@blis/trpc/react";

import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { ClientClientsTable } from "../components/clients/ClientClientsTable";
import { ClientDetailsForm } from "../components/clients/ClientDetailsForm";
import { ClientInvoicesTable } from "../components/clients/ClientInvoicesTable";
import { ClientNotifications } from "../components/clients/ClientNotifications";
import { ClientPanelsTable } from "../components/clients/ClientPanelsTable";
import { ClientTestsTable } from "../components/clients/ClientTestsTable";
import { DeleteClientDialog } from "../components/clients/DeleteClientDialog";
import { SendoutLabTestsTable } from "../components/clients/SendoutLabTestsTable";
import { Section } from "../components/Section";
import { useTabSearch } from "../hooks";
import type { OrgDetail } from "../types";

export function EditClientPage(params: { id: number }) {
  const { data, isPending, error } = trpc.admin.org.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  const org = data;

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/clients" }}>
              Organizations
            </Breadcrumb>
            <Breadcrumb>{org.name}</Breadcrumb>
          </>
        }
        actions={
          <DeleteClientDialog org={org}>
            <Button variant="destructive">Delete</Button>
          </DeleteClientDialog>
        }
      >
        {org.name}
      </Section.Title>
      {org.orgType === "PRIMARY_LAB" || org.orgType === "CLIENT" ? (
        <ClientTabs org={org} />
      ) : org.orgType === "SENDOUT_LAB" ? (
        <SendoutLabTabs org={org} />
      ) : null}
    </>
  );
}

function PrimaryLabTabs({ org }: { org: OrgDetail }) {
  const tabsProps = useTabSearch();
  return (
    <Tabs {...tabsProps} className="flex-1 flex flex-col">
      <TabsList className="w-full justify-start">
        <Tab id="details">Details</Tab>
      </TabsList>
      <TabPanel id="details">
        <ClientDetailsForm key={org.id} org={org} />
      </TabPanel>
    </Tabs>
  );
}

function SendoutLabTabs({ org }: { org: OrgDetail }) {
  const tabsProps = useTabSearch();
  return (
    <Tabs {...tabsProps} className="flex-1 flex flex-col">
      <TabsList className="w-full justify-start">
        <Tab id="details">Details</Tab>
        <Tab id="tests">Tests</Tab>
      </TabsList>
      <TabPanel id="details">
        <ClientDetailsForm key={org.id} org={org} />
      </TabPanel>
      <TabPanel id="tests" className="flex-1 flex flex-col">
        <SendoutLabTestsTable org={org} />
      </TabPanel>
    </Tabs>
  );
}

function ClientTabs({ org }: { org: OrgDetail }) {
  const tabsProps = useTabSearch();
  return (
    <Tabs {...tabsProps} className="flex-1 flex flex-col">
      <TabsList className="w-full justify-start">
        <Tab id="details">Details</Tab>
        {/* <Tabs.Tab id="users">Users</Tabs.Tab> */}
        <Tab id="panels">Panels</Tab>
        <Tab id="tests">Tests</Tab>
        <Tab id="clients">Clients</Tab>
        <Tab id="notifications">Notifications</Tab>
        <Tab id="invoices">Invoices</Tab>
      </TabsList>
      <TabPanel id="details">
        <ClientDetailsForm key={org.id} org={org} />
      </TabPanel>
      <TabPanel id="panels">
        <ClientPanelsTable org={org} />
      </TabPanel>
      <TabPanel id="tests">
        <ClientTestsTable org={org} />
      </TabPanel>
      <TabPanel id="clients">
        <ClientClientsTable org={org} />
      </TabPanel>
      <TabPanel id="notifications" className="flex-1 flex flex-col">
        <ClientNotifications org={org} />
      </TabPanel>
      <TabPanel id="invoices" className="flex-1 flex flex-col">
        <ClientInvoicesTable org={org} />
      </TabPanel>
    </Tabs>
  );
}
